import { zodResolver } from '@hookform/resolvers/zod';
import { Box, TextField, Typography } from '@mui/material';
import { PaintBrush } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  INode,
  ITypeNode,
  useFormBuilder,
} from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';
import { CustomizationForm } from '../FormCustomization/CustomizationForm';

interface TitleSettingsProps {
  node: INode;
}

const TitleSchema = z.object({
  title: z.string(),
  fontFamily: z.string().default('Roboto, sans-serif'),
  fontSize: z.number().min(8).max(72).default(16),
  isBold: z.boolean().default(false),
  isItalic: z.boolean().default(false),
  isUnderlined: z.boolean().default(false),
  textAlign: z.enum(['left', 'center', 'right', 'justify']).default('left'),
  color: z.string().default('#1F2937'),
  backgroundColor: z.string().default('#ffffff'),
  borderColor: z.string().default('#ccc'),
  borderRadius: z.number().min(4).max(72).default(4),
  borderWeight: z.number().min(1).max(10).default(1),
  verticalAlign: z.enum(['top', 'center', 'bottom']).default('top'),
  padding: z.number().min(0).max(72).default(16),
});

export function TitleSettings({ node }: TitleSettingsProps) {
  const { dispatch } = useFormBuilder();
  const [activeTab, setActiveTab] = useState(0);

  const styles = node?.data?.styles;

  const formsMethods = useForm({
    defaultValues: {
      title: node?.data?.title || 'Title',
      fontFamily: styles?.title?.fontFamily || 'Roboto, sans-serif',
      fontSize: styles?.title?.fontSize || 16,
      isBold: styles?.title?.isBold || false,
      isItalic: styles?.title?.isItalic || false,
      isUnderlined: styles?.title?.isUnderlined || false,
      textAlign: styles?.title?.textAlign || 'left',
      color: styles?.title?.color || '#1F2937',
      backgroundColor: styles?.backgroundColor || '#ffffff',
      borderColor: styles?.borderColor || '#ccc',
      borderRadius: styles?.borderRadius || 4,
      borderWeight: styles?.borderWeight || 1,
      verticalAlign: styles?.verticalAlign || 'top',
      padding: styles?.padding !== undefined ? styles.padding : 16,
    },
    resolver: zodResolver(TitleSchema),
  });

  useEffect(() => {
    formsMethods.reset({
      title: node?.data?.title || 'Title',
      fontFamily: styles?.title?.fontFamily || 'Roboto, sans-serif',
      fontSize: styles?.title?.fontSize || 16,
      isBold: styles?.title?.isBold || false,
      isItalic: styles?.title?.isItalic || false,
      isUnderlined: styles?.title?.isUnderlined || false,
      textAlign: styles?.title?.textAlign || 'left',
      color: styles?.title?.color || '#1F2937',
      backgroundColor: styles?.backgroundColor || '#ffffff',
      borderColor: styles?.borderColor || '#ccc',
      borderRadius: styles?.borderRadius || 4,
      borderWeight: styles?.borderWeight || 1,
      verticalAlign: styles?.verticalAlign || 'top',
      padding: styles?.padding !== undefined ? styles.padding : 16,
    });
  }, [node.data, styles, formsMethods]);

  const handleFormChange = (data: any) => {
    const updatedNode = {
      ...node,
      data: {
        ...node.data,
        title: data.title,
        styles: {
          title: {
            fontFamily: data.fontFamily,
            fontSize: data.fontSize,
            isBold: data.isBold,
            isItalic: data.isItalic,
            isUnderlined: data.isUnderlined,
            textAlign: data.textAlign,
            color: data.titleColor,
          },
          backgroundColor: data.backgroundColor,
          borderColor: data.borderColor,
          borderRadius: data.borderRadius,
          borderWeight: data.borderWeight,
          verticalAlign: data.verticalAlign,
          padding: data.padding,
        },
      },
    };

    dispatch({
      type: ActionTypes.UPDATE_NODES_DATA,
      payload: {
        id: node.id,
        updates: { data: updatedNode.data },
      },
    });
  };

  useEffect(() => {
    const subscription = formsMethods.watch(data => {
      if (data && Object.keys(data).length > 0) {
        handleFormChange(data);
      }
    });

    return () => subscription.unsubscribe();
  }, [formsMethods.watch, node.id]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '16px 16px 0 16px',
        }}
      >
        <PaintBrush size={16} color="#101828" />
        <Typography
          style={{ fontSize: '14px', fontWeight: '500', color: '#101828' }}
        >
          Title Settings
        </Typography>
      </Box>

      <FormProvider {...formsMethods}>
        <form style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              padding: '0px 16px 0 16px',
            }}
          >
            <Box
              onClick={() => setActiveTab(0)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 0 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 0 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 0 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Content
            </Box>

            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 1 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 1 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 1 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Design
            </Box>
          </Box>

          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
          />

          {activeTab === 0 && (
            <Box sx={{ padding: '0px 16px 0 16px' }}>
              <Controller
                name="title"
                control={formsMethods.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Title"
                    placeholder="Enter title..."
                    fullWidth
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        margin: '0px',
                        height: '38px',
                        fontSize: '14px',
                      },
                    }}
                  />
                )}
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <CustomizationForm
                control={formsMethods.control}
                type={ITypeNode.TITLE}
              />
            </Box>
          )}
        </form>
      </FormProvider>
    </>
  );
}
